import React from 'react';
import { push } from 'redux-first-history';
import { connect } from '../../redux/store';
import { compose, lifecycle, withHandlers, withProps } from 'recompose';
import { openModalForm, TimeAgo, Utils, C } from '@kineticdata/bundle-common';
import {
  I18n,
  isLockable,
  isLocked,
  isLockedByMe,
  getTimeLeft,
} from '@kineticdata/react';
import { actions } from '../../redux/modules/submission';

const CompletedDateItem = ({ submission }) =>
  !!submission &&
  submission.coreState === C.CORE_STATE_SUBMITTED && (
    <>
      <TimeAgo timestamp={submission.submittedAt} />
      {!!submission.values[C.FIELD_COMPLETED_BY_NAME] && (
        <>
          <span className="font-weight-normal">
            {' '}
            <I18n>by</I18n>{' '}
          </span>
          <span>{submission.values[C.FIELD_COMPLETED_BY_NAME]}</span>
        </>
      )}
    </>
  );

const ApprovalMetaWrapperComponent = ({ submission, children, ...props }) => {
  return children(props);
};

export const mapStateToProps = (state, props) => ({
  kapp: state.app.kapp,
  profile: state.app.profile,
  appLocation: state.app.location,
});

export const mapDispatchToProps = {
  deleteSubmission: actions.deleteSubmissionRequest,
  push,
};

export const ApprovalMetaWrapper = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  lifecycle({
    componentDidMount() {},
    componentWillUnmount() {},
  }),
  withProps(({ submission, profile, navigate }) => {
    const hasRequestParent =
      !!submission &&
      !!submission.parent &&
      submission.parent.type === C.REQUEST_FORM_TYPE;
    const unresolvedIssues =
      hasRequestParent && Array.isArray(submission.parent.descendants)
        ? submission.parent.descendants.filter(
            d => d.type === 'Issue' && d.coreState === 'Draft',
          )
        : [];
    return {
      navigate: navigate || push,
      isLockable: isLockable(submission),
      isLocked: isLocked(submission),
      isLockedByMe: isLockedByMe(submission),
      lockMinutesLeft: Math.ceil(getTimeLeft(submission) / 60000),
      isCompletedByMe: (
        (!!submission && submission.values[C.FIELD_APPROVAL_USERNAMES]) ||
        ''
      )
        .split('\n')
        .includes(profile.username),
      hasRequestParent,
      unresolvedIssues,
      hideRequestReview: Utils.hasAttributeValue(
        (submission && submission.form) || {},
        C.FORM_ATTR_EXCLUDE_REQUEST_REVIEW,
        ['True', 'Yes'],
        true,
      ),
      usesContentManager:
        submission &&
        ((submission.form && submission.form.slug === 'approve-catalog') ||
          (submission.parent &&
            submission.parent.form &&
            submission.parent.form.type === 'Datastore' &&
            [
              C.FORM_SLUG_PORTFOLIO,
              C.FORM_SLUG_SERVICE,
              C.FORM_SLUG_APPENDIX,
            ].includes(submission.parent.form.slug))),

      canAddComment:
        hasRequestParent && submission.parent.coreState !== C.CORE_STATE_DRAFT,
      canAddIssue:
        hasRequestParent &&
        submission.parent.coreState === C.CORE_STATE_SUBMITTED &&
        !!submission.parent.form &&
        !Utils.hasAttributeValue(
          submission.parent.form,
          C.FORM_ATTR_EXCLUDE_ISSUE_BUTTON,
          ['true', 'yes'],
          true,
        ),
    };
  }),
  withHandlers(() => {
    // Handler for adding a comment by opening a modal form
    const handleAddComment = props => ({ preventDefault } = {}) =>
      props.canAddComment
        ? e => {
            if (preventDefault) e.preventDefault();
            openModalForm({
              ...C.COMMENT_FORM_CONFIG,
              kappSlug: props.kapp && props.kapp.slug,
              values: {
                [C.FIELD_RELATED_SUBMISSION_ID]: props.submission.parent.id,
              },
              duration: 5000,
            });
          }
        : undefined;
    // Handler for adding issues
    const handleAddIssue = props => ({ preventDefault } = {}) =>
      props.canAddIssue
        ? e => {
            if (preventDefault) e.preventDefault();
            openModalForm({
              ...C.ISSUE_CREATE_FORM_CONFIG,
              kappSlug: props.kapp && props.kapp.slug,
              values: {
                [C.FIELD_RELATED_SUBMISSION_ID]: props.submission.parent.id,
              },
              duration: 5000,
            });
          }
        : undefined;

    return {
      handleAddComment,
      handleAddIssue,
    };
  }),
  withHandlers(() => {
    return {
      showCreatedDate: ({ submission }) => () => (
        <TimeAgo timestamp={submission.createdAt} />
      ),
      showSubmitter: ({ submission }) => () =>
        submission.values[C.FIELD_APPROVAL_SUBMITTER_NAME],
      showCompletedDate: ({ submission }) => () =>
        submission.coreState === C.CORE_STATE_SUBMITTED && (
          <CompletedDateItem submission={submission} />
        ),
      showApprovalsCount: ({ submission }) => () =>
        !!submission.values[C.FIELD_APPROVAL_NUMBER_NEEDED] &&
        parseInt(submission.values[C.FIELD_APPROVAL_NUMBER_NEEDED], 10) > 1 && (
          <I18n
            render={translate =>
              `${submission.values[C.FIELD_APPROVAL_NUMBER_RECEIVED] ||
                0} ${translate('of')} ${
                submission.values[C.FIELD_APPROVAL_NUMBER_NEEDED]
              }`
            }
          />
        ),
      showIssues: ({ unresolvedIssues }) => () =>
        unresolvedIssues.length > 0 && (
          <span className="text-danger">
            <I18n>Unresolved Issue(s)</I18n>
          </span>
        ),
    };
  }),
)(ApprovalMetaWrapperComponent);
